/* eslint-disable react-hooks/rules-of-hooks */
import { useMemo } from 'react';

import { TransactionSide } from '@graphql/requests';

import { WebChatQuestionnaire } from '@ui-furniture/common/questionnaire/web-chat-questionnaire';

export type ReservationAgreementQuestionnaireData = {
  hasOfferAccepted: boolean;
  address?: string;
  depositAmountInPounds?: number;
  additionalConditions?: string;
  ownerSide: TransactionSide;
  addressId?: string;
};

export const useRaQuestionnaire = ({
  submit,
}: {
  submit: (data: ReservationAgreementQuestionnaireData) => Promise<boolean>;
}) => {
  return useMemo(() => {
    return new WebChatQuestionnaire<
      Partial<ReservationAgreementQuestionnaireData>,
      | 'userType'
      | 'propertyAddress'
      | 'depositAmount'
      | 'hasAdditionalConditions'
      | 'additionalConditions'
      | 'submit'
    >(
      ({
        say,
        useData,
        useAnswer,
        setNext,
        updateData,
        withValidation,
        askQuestion,
        getAddressInput,
        getBoolInput,
        getButtonInput,
        getCurrencyInput,
        getSingleSelectInput,
        getTextInput,
        reportAnswer,
      }) => ({
        startQuestion: 'userType',
        list: {
          userType: askQuestion({
            before: [
              say([
                'Hi 👋',
                'Let’s draft your HousecureProtect policy!',
                'Firstly, are you the property buyer or seller?',
              ]),
            ],
            getUserInput: getSingleSelectInput<TransactionSide>({
              options: [
                { label: 'Buyer', value: TransactionSide.Purchase },
                {
                  label: 'Seller',
                  value: TransactionSide.Sale,
                },
              ],
            }),
          }).respond({
            after: [
              useAnswer(({ value: ownerSide }) =>
                updateData({
                  ownerSide,
                })
              ),
              useAnswer(({ value }) =>
                reportAnswer({
                  questionnaireName: 'Protect',
                  questionId: 'ownerSide',
                  answer: value,
                })
              ),
            ],
            next: useData(({ ownerSide }) => {
              return ownerSide === TransactionSide.Purchase
                ? setNext('depositAmount')
                : setNext('propertyAddress');
            }),
          }),
          propertyAddress: askQuestion({
            before: [say(['What’s the address of your property?'])],
            getUserInput: getAddressInput(),
          }).respond({
            after: [
              useAnswer(({ id: addressId, address }) =>
                updateData({
                  addressId,
                  address,
                })
              ),
              useAnswer(({ address }) =>
                reportAnswer({
                  questionnaireName: 'Protect',
                  questionId: 'propertyAddress',
                  answer: address,
                })
              ),
              say(['Ok, great']),
            ],
            next: setNext('depositAmount'),
          }),
          depositAmount: askQuestion({
            before: [
              say([
                'Next we need to know how much money you want both sides to put in as a deposit. This is what’s used if any compensation is needed.',
                'Don’t worry, any money not used as compensation is returned',
                'And we only take a fee if we successfully help you exchange. The fee is £200-£300 due from both parties',
                'So how much would you like the deposit to be? (we recommend around £3,000 because this is the average amount of money lost in a transaction)',
              ]),
            ],
            getUserInput: withValidation(
              ({ value: depositAmount }) => {
                if (!depositAmount) {
                  return [say(['Please enter a deposit amount'])];
                }
                if (depositAmount < 1) {
                  return [say(['Please enter a valid deposit amount'])];
                }
                if (depositAmount > 20000) {
                  return [
                    say(['Please enter a deposit amount less than £20,000']),
                  ];
                }
                if (depositAmount < 300) {
                  return [
                    say(['Please enter a deposit amount more than £300']),
                  ];
                }
                return null;
              },
              getCurrencyInput({
                defaultValue: '3000',
              })
            ),
          }).respond({
            after: [
              useAnswer(({ value: depositAmountInPounds }) =>
                updateData({
                  depositAmountInPounds,
                })
              ),
              useAnswer(({ text: value }) =>
                reportAnswer({
                  questionnaireName: 'Protect',
                  questionId: 'depositAmount',
                  answer: value,
                })
              ),
              say(['Ok, great']),
            ],
            next: setNext('hasAdditionalConditions'),
          }),
          hasAdditionalConditions: askQuestion({
            before: [
              say([
                'Do you have any additional conditions you’d like to add to the policy?',
                'e.g. this could be a specific date you must exchange or complete by',
                '(Most people don’t but you might have something particularly important to you)',
              ]),
            ],
            getUserInput: getBoolInput(),
          }).respond({
            after: [
              useAnswer(({ value }) =>
                reportAnswer({
                  questionnaireName: 'Protect',
                  questionId: 'hasAdditionalConditions',
                  answer: value ? 'yes' : 'no',
                })
              ),
            ],
            next: useAnswer(({ value: hasAdditionalConditions }) =>
              hasAdditionalConditions
                ? setNext('additionalConditions')
                : setNext('submit')
            ),
          }),
          additionalConditions: askQuestion({
            before: [say(['No problem, what are the conditions?'])],
            getUserInput: getTextInput(),
          }).respond({
            after: [
              useAnswer(({ text: additionalConditions }) =>
                updateData({
                  additionalConditions,
                })
              ),
              useAnswer(({ text: value }) =>
                reportAnswer({
                  questionnaireName: 'Protect',
                  questionId: 'additionalConditions',
                  answer: value,
                })
              ),
            ],
            next: setNext('submit'),
          }),

          submit: askQuestion({
            before: [
              say([
                'That’s all the details we need to draft your policy',
                'We’ll give you a chance to review the information you’ve supplied on the next page and then all we need is the relevant contact information',
              ]),
            ],
            getUserInput: useData((data) =>
              getButtonInput({
                label: 'Take me to review',
                submit: () => submit(data as any),
              })
            ),
          }).respond({
            next: null,
          }),
        },
      })
    );
  }, [submit]);
};
